/**
 * TimelineMultiTrack — Multi-track video editor timeline
 *
 * Renders a professional timeline with:
 * - Time ruler (top + bottom)
 * - Audio track (segment rectangles)
 * - Pages track (route bars)
 * - Interaction track (click markers)
 * - Synchronized playhead
 * - Click-to-seek anywhere
 */
import React, { useMemo, useRef, useCallback } from 'react';
import { Volume2, FileText, MousePointer2 } from 'lucide-react';
import type { useSegmentAudio } from '../hooks/useSegmentAudio';

// ── Types ──

export interface TimelineSegmentMeta {
  index: number;
  startSec: number;
  endSec: number;
  durationSec: number;
  text: string;
  health: string;
}

export interface TimelineEvent {
  time: number;       // seconds from start
  type: string;       // 'click' | 'route'
  detail: string;     // selector or pathname
  label: string;      // human description
  meta: any;
}

interface TimelineMultiTrackProps {
  duration: number;
  currentTime: number;
  segmentsMeta: TimelineSegmentMeta[];
  timelineEvents: TimelineEvent[];
  audioSource: 'bruto' | 'preview' | 'premium';
  segAudio?: ReturnType<typeof useSegmentAudio>;
  onSeek: (timeSec: number) => void;
  pxPerSec: number;
  activeSegIdx: number | null;
  voiceName?: string;
}

// ── Helpers ──

function fmtTime(sec: number): string {
  if (!sec || !isFinite(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function fmtPreciseTime(sec: number): string {
  if (!sec || !isFinite(sec)) return '0:00.0';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s < 10 ? '0' : ''}${s.toFixed(1)}`;
}

function hashToColor(str: string): string {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  const hue = Math.abs(h) % 360;
  return `hsl(${hue}, 55%, 55%)`;
}

function shortPath(path: string): string {
  const parts = path.replace(/\?.*$/, '').split('/').filter(Boolean);
  return parts.length > 0 ? `/${parts[parts.length - 1]}` : '/';
}

// ── Component ──

export function TimelineMultiTrack(props: TimelineMultiTrackProps) {
  const {
    duration, currentTime, segmentsMeta, timelineEvents,
    audioSource, segAudio, onSeek, pxPerSec, activeSegIdx, voiceName
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);

  // Total width in px
  const totalWidth = Math.max(200, duration * pxPerSec);

  // ── Route segments (start→end bars) ──
  const routeSegments = useMemo(() => {
    const routes = timelineEvents.filter(e => e.type === 'route');
    if (routes.length === 0) return [];

    const result: Array<{ startSec: number; endSec: number; path: string; label: string; color: string }> = [];
    for (let i = 0; i < routes.length; i++) {
      const startSec = routes[i].time;
      const endSec = i + 1 < routes.length ? routes[i + 1].time : duration;
      const path = routes[i].detail || routes[i].meta?.pathname || '';
      result.push({
        startSec,
        endSec,
        path,
        label: routes[i].label,
        color: hashToColor(path)
      });
    }
    return result;
  }, [timelineEvents, duration]);

  // ── Click events ──
  const clickEvents = useMemo(() => {
    return timelineEvents.filter(e => e.type === 'click');
  }, [timelineEvents]);

  // ── Time ruler marks ──
  const rulerMarks = useMemo(() => {
    if (duration <= 0) return [];
    // Auto-scale interval: 1s, 2s, 5s, 10s, 15s, 30s, 60s based on pxPerSec
    const minPxBetweenMarks = 40;
    const intervals = [1, 2, 5, 10, 15, 30, 60, 120, 300];
    let interval = 5;
    for (const iv of intervals) {
      if (iv * pxPerSec >= minPxBetweenMarks) {
        interval = iv;
        break;
      }
    }
    const marks: Array<{ sec: number; label: string; major: boolean }> = [];
    for (let t = 0; t <= duration; t += interval) {
      marks.push({ sec: t, label: fmtTime(t), major: t % (interval * 2) === 0 || t === 0 });
    }
    return marks;
  }, [duration, pxPerSec]);

  // ── Seek handler ──
  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container || duration <= 0) return;
    const rect = container.getBoundingClientRect();
    const scrollLeft = container.scrollLeft;
    const clickX = e.clientX - rect.left + scrollLeft;
    const timeSec = Math.max(0, Math.min(duration, clickX / pxPerSec));
    onSeek(timeSec);
  }, [duration, pxPerSec, onSeek]);

  // ── Playhead position ──
  const playheadLeft = currentTime * pxPerSec;

  // ── Track label style ──
  const trackLabelStyle: React.CSSProperties = {
    width: 22, minWidth: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#0a0a0a', borderRight: '1px solid #1e293b',
    flexShrink: 0, zIndex: 2
  };

  // ── Ruler ──
  const renderRuler = () => (
    <div style={{
      position: 'relative', height: 18, width: totalWidth,
      borderBottom: '1px solid #1e293b', userSelect: 'none'
    }}>
      {rulerMarks.map((m, i) => (
        <div key={i} style={{
          position: 'absolute', left: m.sec * pxPerSec, top: 0, height: '100%',
          display: 'flex', flexDirection: 'column', alignItems: 'center'
        }}>
          <div style={{
            width: 1, height: m.major ? 10 : 6,
            background: m.major ? '#475569' : '#1e293b',
            marginTop: 'auto'
          }} />
          {m.major && (
            <span style={{
              position: 'absolute', top: 1, left: 2,
              fontSize: '.42rem', color: '#64748b', whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums'
            }}>
              {m.label}
            </span>
          )}
        </div>
      ))}
    </div>
  );

  // ── Audio source color ──
  const sourceColor = audioSource === 'bruto' ? '#94a3b8'
    : audioSource === 'preview' ? '#eab308' : '#22c55e';

  return (
    <div style={{
      background: '#0a0a0a', borderRadius: 6,
      border: '1px solid #1e293b',
      fontSize: '.5rem', overflow: 'hidden',
      marginTop: 4
    }}>
      {/* Scrollable container */}
      <div
        ref={containerRef}
        onClick={handleSeek}
        style={{
          overflowX: 'auto', overflowY: 'hidden',
          position: 'relative', cursor: 'crosshair'
        }}
      >
        <div style={{ width: totalWidth, position: 'relative' }}>

          {/* ── Top Ruler ── */}
          <div style={{ display: 'flex' }}>
            <div style={{ ...trackLabelStyle, height: 18 }} />
            {renderRuler()}
          </div>

          {/* ── Track 1: Slots de Vídeo ── */}
          <div style={{ display: 'flex', borderTop: '1px solid #1e293b' }}>
            <div style={{ ...trackLabelStyle, height: 22 }} title="Slots de Vídeo">
              <span style={{ fontSize: '.6rem' }}>📹</span>
            </div>
            <div style={{ position: 'relative', height: 22, width: totalWidth, background: '#0f0f0f' }}>
              {segmentsMeta.map(seg => {
                const left = seg.startSec * pxPerSec;
                const slotWidth = Math.max(2, seg.durationSec * pxPerSec);
                const isActive = activeSegIdx === seg.index;

                return (
                  <div
                    key={seg.index}
                    title={`#${seg.index + 1} — Slot: ${seg.durationSec.toFixed(1)}s (${fmtPreciseTime(seg.startSec)}→${fmtPreciseTime(seg.endSec)})`}
                    style={{
                      position: 'absolute', left, width: slotWidth,
                      top: 2, height: 18, borderRadius: 3,
                      background: 'rgba(148, 163, 184, 0.1)',
                      border: isActive ? `1px solid ${sourceColor}` : '1px solid #334155',
                      transition: 'background 0.15s, border 0.15s',
                      display: 'flex', alignItems: 'center',
                      padding: '0 3px', overflow: 'hidden'
                    }}
                  >
                    {slotWidth > 20 && (
                      <span style={{
                        fontSize: '.38rem', color: '#94a3b8',
                        whiteSpace: 'nowrap', overflow: 'hidden',
                        textOverflow: 'ellipsis', lineHeight: 1
                      }}>
                        #{seg.index + 1}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Track 2: Áudio real / Fala ── */}
          <div style={{ display: 'flex', borderTop: '1px solid #1e293b' }}>
            <div style={{ ...trackLabelStyle, height: 22 }} title="Fala / Áudio">
              <Volume2 size={10} style={{ color: sourceColor }} />
            </div>
            <div style={{ position: 'relative', height: 22, width: totalWidth, background: '#0f0f0f' }}>
              {segmentsMeta.map(seg => {
                const left = seg.startSec * pxPerSec;
                const isActive = activeSegIdx === seg.index;

                // Speech estimate or real duration
                let speechSec = seg.durationSec;
                let fillColor = 'rgba(148, 163, 184, 0.2)';
                
                if (audioSource !== 'bruto' && segAudio) {
                  const realDuration = segAudio.getAudioDuration(seg.index, audioSource, voiceName || '');
                  const words = seg.text.trim().split(/\s+/).filter(Boolean).length;
                  const estimated = words / 2.3;
                  speechSec = realDuration ?? estimated;

                  const status = segAudio.getGenerationStatus(seg.index, audioSource);
                  if (status === 'done') {
                    fillColor = audioSource === 'preview'
                      ? 'rgba(234, 179, 8, 0.35)' : 'rgba(34, 197, 94, 0.35)';
                  } else if (status === 'generating') {
                    fillColor = 'rgba(56, 189, 248, 0.25)';
                  }
                }

                const speechWidth = Math.max(2, speechSec * pxPerSec);
                const hasOverflow = speechSec > seg.durationSec * 1.05;

                return (
                  <div
                    key={seg.index}
                    title={`#${seg.index + 1} Áudio — ${speechSec.toFixed(1)}s (Disponível: ${seg.durationSec.toFixed(1)}s)`}
                    style={{
                      position: 'absolute', left, width: speechWidth,
                      top: 2, height: 18, borderRadius: 3,
                      background: fillColor,
                      border: hasOverflow 
                        ? '1px solid #ef4444' 
                        : isActive ? `1px solid ${sourceColor}` : '1px solid #334155',
                      transition: 'background 0.15s, border 0.15s',
                      display: 'flex', alignItems: 'center',
                      padding: '0 5px', overflow: 'hidden',
                      zIndex: hasOverflow ? 2 : 1
                    }}
                  >
                    {speechWidth > 40 && (
                      <span style={{
                        fontSize: '.38rem', color: hasOverflow ? '#f87171' : '#cbd5e1',
                        whiteSpace: 'nowrap', overflow: 'hidden',
                        textOverflow: 'ellipsis', lineHeight: 1
                      }}>
                        ~{speechSec.toFixed(1)}s ({fmtPreciseTime(seg.startSec)} → {fmtPreciseTime(seg.startSec + speechSec)})
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Track 3: Congelamentos (❄️) ── */}
          {audioSource !== 'bruto' && (
            <div style={{ display: 'flex', borderTop: '1px solid #1e293b' }}>
              <div style={{ ...trackLabelStyle, height: 20 }} title="Congelamentos (❄️)">
                <span style={{ fontSize: '.6rem' }}>❄️</span>
              </div>
              <div style={{ position: 'relative', height: 20, width: totalWidth, background: '#0f0f0f' }}>
                {segmentsMeta.map(seg => {
                  let speechSec = seg.durationSec;
                  if (segAudio) {
                    const realDuration = segAudio.getAudioDuration(seg.index, audioSource, voiceName || '');
                    const words = seg.text.trim().split(/\s+/).filter(Boolean).length;
                    const estimated = words / 2.3;
                    speechSec = realDuration ?? estimated;
                  }

                  const hasOverflow = speechSec > seg.durationSec * 1.05;
                  if (!hasOverflow) return null;

                  const freezeDuration = speechSec - seg.durationSec;
                  const left = seg.endSec * pxPerSec;
                  const width = Math.max(2, freezeDuration * pxPerSec);

                  return (
                    <div
                      key={seg.index}
                      title={`#${seg.index + 1} Congelamento de tela — Vídeo pausará por +${freezeDuration.toFixed(1)}s`}
                      style={{
                        position: 'absolute', left, width,
                        top: 2, height: 16, borderRadius: 2,
                        background: 'rgba(56, 189, 248, 0.15)',
                        border: '1px solid rgba(56, 189, 248, 0.35)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#38bdf8', overflow: 'hidden'
                      }}
                    >
                      {width > 12 && (
                        <span style={{ fontSize: '.38rem', fontWeight: 800 }}>
                          ❄️ +{freezeDuration.toFixed(1)}s
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Track 2: Pages/Routes ── */}
          {routeSegments.length > 0 && (
            <div style={{ display: 'flex', borderTop: '1px solid #1e293b' }}>
              <div style={{ ...trackLabelStyle, height: 16 }} title="Páginas">
                <FileText size={9} style={{ color: '#a78bfa' }} />
              </div>
              <div style={{ position: 'relative', height: 16, width: totalWidth, background: '#0f0f0f' }}>
                {routeSegments.map((rs, i) => {
                  const left = rs.startSec * pxPerSec;
                  const width = Math.max(2, (rs.endSec - rs.startSec) * pxPerSec);
                  return (
                    <div
                      key={i}
                      title={rs.label}
                      style={{
                        position: 'absolute', left, width,
                        top: 2, height: 12, borderRadius: 2,
                        background: `${rs.color}33`,
                        border: `1px solid ${rs.color}55`,
                        display: 'flex', alignItems: 'center',
                        padding: '0 3px', overflow: 'hidden'
                      }}
                    >
                      {width > 30 && (
                        <span style={{
                          fontSize: '.36rem', color: rs.color,
                          whiteSpace: 'nowrap', overflow: 'hidden',
                          textOverflow: 'ellipsis', fontWeight: 600
                        }}>
                          {shortPath(rs.path)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Track 3: Interactions (clicks) ── */}
          {clickEvents.length > 0 && (
            <div style={{ display: 'flex', borderTop: '1px solid #1e293b' }}>
              <div style={{ ...trackLabelStyle, height: 14 }} title="Interações">
                <MousePointer2 size={8} style={{ color: '#f97316' }} />
              </div>
              <div style={{ position: 'relative', height: 14, width: totalWidth, background: '#0f0f0f' }}>
                {clickEvents.map((ev, i) => {
                  const left = ev.time * pxPerSec;
                  return (
                    <div
                      key={i}
                      title={ev.label}
                      style={{
                        position: 'absolute', left: left - 3,
                        top: 3, width: 6, height: 6, borderRadius: '50%',
                        background: '#f97316',
                        border: '1px solid #ea580c',
                        cursor: 'pointer',
                        transition: 'transform 0.1s'
                      }}
                      onMouseEnter={(e) => {
                        (e.target as HTMLElement).style.transform = 'scale(1.5)';
                      }}
                      onMouseLeave={(e) => {
                        (e.target as HTMLElement).style.transform = 'scale(1)';
                      }}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Bottom Ruler ── */}
          <div style={{ display: 'flex', borderTop: '1px solid #1e293b' }}>
            <div style={{ ...trackLabelStyle, height: 18 }} />
            <div style={{
              position: 'relative', height: 18, width: totalWidth,
              userSelect: 'none'
            }}>
              {rulerMarks.map((m, i) => (
                <div key={i} style={{
                  position: 'absolute', left: m.sec * pxPerSec, top: 0, height: '100%',
                  display: 'flex', flexDirection: 'column', alignItems: 'center'
                }}>
                  <div style={{
                    width: 1, height: m.major ? 10 : 6,
                    background: m.major ? '#475569' : '#1e293b'
                  }} />
                  {m.major && (
                    <span style={{
                      position: 'absolute', bottom: 1, left: 2,
                      fontSize: '.42rem', color: '#64748b', whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums'
                    }}>
                      {m.label}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* ── Playhead ── */}
          {duration > 0 && (
            <div style={{
              position: 'absolute', top: 0, bottom: 0,
              left: playheadLeft + 22, // offset for track labels
              width: 1, background: '#ef4444',
              pointerEvents: 'none', zIndex: 10,
              boxShadow: '0 0 4px rgba(239,68,68,0.5)'
            }}>
              {/* Playhead triangle */}
              <div style={{
                position: 'absolute', top: -1, left: -4,
                width: 0, height: 0,
                borderLeft: '4px solid transparent',
                borderRight: '4px solid transparent',
                borderTop: '5px solid #ef4444'
              }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
